import type { ModuleId } from "../types";

export type AppView = "home" | "module" | "lite";

/** Os dois grandes blocos do modo LITE. Cada um aponta para o modulo que
 *  detem a logica de dose — o LITE nunca reimplementa calculo. */
export type LiteGroup = "adulto" | "ped";

export const LITE_MODULE: Record<LiteGroup, ModuleId> = {
  adulto: "calculadoras",
  ped: "pediatria"
};

export interface AppRoute {
  view: AppView;
  /** Sempre presente: usado como modulo "ativo" mesmo quando view=home, para
   *  manter sidebar consistente e permitir back/forward. */
  moduleId: ModuleId;
  sectionId?: string;
  /** view=lite: qual bloco esta aberto. Ausente = tela de escolha. */
  liteGroup?: LiteGroup;
  /** view=lite: chip/modal a abrir dentro do modulo (data-chip no 14,
   *  data-modal no 15). Ausente = indice de calculadoras do bloco. */
  calcId?: string;
}

const DEFAULT_MODULE: ModuleId = "modulo-01";
const MODULE_IDS = new Set<ModuleId>([
  "modulo-01",
  "modulo-02",
  "modulo-03",
  "modulo-04",
  "modulo-05",
  "modulo-06",
  "modulo-07",
  "modulo-08",
  "modulo-09",
  "modulo-10",
  "modulo-11",
  "modulo-12",
  "modulo-13",
  "modulo-14",
  "modulo-15",
  "calculadoras",
  "pediatria",
  "caderno",
  "referencias"
]);

export function parseHashRoute(hash: string): AppRoute {
  const clean = hash.replace(/^#\/?/, "");
  const parts = clean.split("/").filter(Boolean);

  // Rota explicita de home: "#/home" ou "#home"
  if (parts[0] === "home") {
    return { view: "home", moduleId: DEFAULT_MODULE };
  }

  // Modo LITE: "#/lite" | "#/lite/adulto" | "#/lite/adulto/continua"
  if (parts[0] === "lite") {
    const group = parts[1] === "adulto" || parts[1] === "ped" ? (parts[1] as LiteGroup) : undefined;
    if (!group) return { view: "lite", moduleId: LITE_MODULE.adulto };
    return {
      view: "lite",
      moduleId: LITE_MODULE[group],
      liteGroup: group,
      calcId: parts[2]
    };
  }

  if (parts[0] === "module" && MODULE_IDS.has(parts[1] as ModuleId)) {
    return {
      view: "module",
      moduleId: parts[1] as ModuleId,
      sectionId: parts[2]
    };
  }

  if (MODULE_IDS.has(parts[0] as ModuleId)) {
    return {
      view: "module",
      moduleId: parts[0] as ModuleId,
      sectionId: parts[1]
    };
  }

  // Hash vazio = home (primeira impressao do usuario logado).
  return { view: "home", moduleId: DEFAULT_MODULE };
}

export function routeToHash(route: AppRoute): string {
  if (route.view === "home") return "#/home";
  if (route.view === "lite") {
    if (!route.liteGroup) return "#/lite";
    return route.calcId
      ? `#/lite/${route.liteGroup}/${route.calcId}`
      : `#/lite/${route.liteGroup}`;
  }
  return route.sectionId
    ? `#/module/${route.moduleId}/${route.sectionId}`
    : `#/module/${route.moduleId}`;
}
